import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, DatePicker, Modal, Result, Space, Table, Tag, Typography } from "antd";
import locale from "antd/es/date-picker/locale/pt_BR";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { request } from "../../functions/request";

const { RangePicker } = DatePicker;
const { Text, Title, Paragraph } = Typography;

const DIAS_SEMANA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const dataBr = (iso) => {
  const d = dayjs(iso);
  return `${d.format("DD/MM/YYYY")} (${DIAS_SEMANA[d.day()]})`;
};

/**
 * Correções › Robô diário.
 *
 * O robô das 00:00 (RepeatPrice, no ApiNode) repete as cotações do último
 * dia para o dia seguinte, para as séries não terem buraco. Quando ele não
 * roda, o dia fica vazio. Esta tela mostra esses dias e preenche só o que
 * falta, copiando do dia anterior como o robô faria — nada é repetido.
 */
export default function RoboDiario() {
  const [periodo, setPeriodo] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [resultado, setResultado] = useState(null);
  const [verificando, setVerificando] = useState(false);
  const [preenchendo, setPreenchendo] = useState(null); // dia em andamento

  const verificar = useCallback(async () => {
    if (!periodo?.[0] || !periodo?.[1]) return;
    setVerificando(true);
    try {
      const r = await request(
        `/robo_diario/diagnostico?inicio=${periodo[0].format("YYYY-MM-DD")}&fim=${periodo[1].format("YYYY-MM-DD")}`,
        { method: "GET" }
      );
      setResultado(r);
    } catch (erro) {
      console.log(erro);
      toast.error("Não foi possível verificar o período.");
    } finally {
      setVerificando(false);
    }
  }, [periodo]);

  useEffect(() => {
    verificar();
    // só na abertura; depois, pelo botão
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const executar = async (dia, simular) => {
    setPreenchendo(dia);
    try {
      const r = await request("/robo_diario/preencher", {
        method: "POST",
        body: JSON.stringify({ dia, simular }),
      });
      if (simular) {
        Modal.info({
          title: `Simulação de ${dataBr(dia)}`,
          width: 640,
          content: (
            <div>
              <Paragraph>
                Entrariam <b>{r.inseridas}</b> cotações em {r.tabelas.length} tabela(s). Nada foi gravado.
              </Paragraph>
              <Table
                size="small"
                pagination={false}
                rowKey="tabela"
                dataSource={r.tabelas}
                scroll={{ y: 300 }}
                columns={[
                  { title: "Tabela", dataIndex: "tabela" },
                  { title: "Copiar de", dataIndex: "fonte", render: (v) => dayjs(v).format("DD/MM/YYYY") },
                  { title: "Cotações", dataIndex: "inseridas", align: "right" },
                ]}
              />
            </div>
          ),
        });
      } else {
        toast.success(`${dataBr(dia)}: ${r.inseridas} cotação(ões) inserida(s) em ${r.tabelas.length} tabela(s).`);
        verificar();
      }
    } catch (erro) {
      console.log(erro);
      toast.error(simular ? "Não foi possível simular." : "Não foi possível preencher o dia. Nada foi gravado.");
    } finally {
      setPreenchendo(null);
    }
  };

  const confirmarPreenchimento = (linha) => {
    Modal.confirm({
      title: `Gerar as cotações de ${dataBr(linha.dia)}?`,
      content: (
        <div>
          <p>
            Vão entrar <b>{linha.faltam}</b> cotações em {linha.tabelas.length} tabela(s), copiadas do dia anterior
            (como o robô faria). Só entra o que falta: o que já existe no dia não é repetido.
          </p>
          <p style={{ marginBottom: 0 }}>
            <Text type="secondary">Leva alguns segundos. Se der qualquer erro, nada é gravado.</Text>
          </p>
        </div>
      ),
      okText: "Gerar cotações",
      cancelText: "Cancelar",
      onOk: () => executar(linha.dia, false),
    });
  };

  const colunas = [
    { title: "Dia", dataIndex: "dia", render: (v) => <Text strong>{dataBr(v)}</Text> },
    {
      title: "Tabelas com falta",
      align: "right",
      render: (_, l) => `${l.tabelas.length} de ${resultado?.tabelas ?? "?"}`,
    },
    { title: "Cotações faltando", dataIndex: "faltam", align: "right" },
    {
      title: "",
      align: "right",
      render: (_, l) => (
        <Space>
          <Button
            onClick={() => executar(l.dia, true)}
            loading={preenchendo === l.dia}
            disabled={Boolean(preenchendo) && preenchendo !== l.dia}
          >
            Simular
          </Button>
          <Button
            type="primary"
            onClick={() => confirmarPreenchimento(l)}
            loading={preenchendo === l.dia}
            disabled={Boolean(preenchendo) && preenchendo !== l.dia}
          >
            Gerar cotações
          </Button>
        </Space>
      ),
    },
  ];

  const detalhes = (l) => (
    <Table
      size="small"
      pagination={false}
      rowKey="tabela"
      dataSource={l.tabelas}
      columns={[
        { title: "Tabela", dataIndex: "tabela" },
        { title: "Faltando", dataIndex: "faltam", align: "right" },
        {
          title: "Já no dia",
          dataIndex: "existentes",
          align: "right",
          render: (v) => (v ? <Tag color="blue">{v} (mantidas)</Tag> : "—"),
        },
        { title: "Copiar de", dataIndex: "fonte", render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
      ]}
    />
  );

  return (
    <Card style={{ margin: 16 }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Robô diário</Title>
          <Text type="secondary">
            Dias em que o robô da meia-noite não repetiu as cotações. "Gerar cotações" copia do dia anterior só o que
            falta.
          </Text>
        </div>

        <Space wrap>
          <RangePicker
            locale={locale}
            format="DD/MM/YYYY"
            value={periodo}
            onChange={setPeriodo}
            disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            allowClear={false}
          />
          <Button type="primary" onClick={verificar} loading={verificando}>
            Verificar
          </Button>
        </Space>

        {resultado && resultado.dias.length === 0 && (
          <Result
            status="success"
            title="Nenhum dia com cotações faltando"
            subTitle={`As ${resultado.tabelas} tabelas do robô estão completas no período.`}
          />
        )}

        {resultado && resultado.dias.length > 0 && (
          <>
            <Alert
              type="warning"
              showIcon
              message={`${resultado.dias.length} dia(s) com cotações faltando`}
              description="Abra a linha para ver cada tabela. Use Simular para conferir antes de gravar."
            />
            <Table
              rowKey="dia"
              columns={colunas}
              dataSource={resultado.dias}
              pagination={false}
              expandable={{ expandedRowRender: detalhes }}
              loading={verificando}
            />
          </>
        )}
      </Space>
    </Card>
  );
}
